<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { ArrowRight, BadgeCheck, FileText, Loader2, Lock, ShieldCheck, UploadCloud } from 'lucide-vue-next'
import api from '@/lib/api'

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.tiff,.tif'
const MAX_BYTES = 3 * 1024 * 1024

const file = ref(null)
const pageCount = ref(0)
const dragOver = ref(false)
const loading = ref(false)
const error = ref('')
const result = ref(null)
const token = ref('')
const widgetEl = ref(null)
const turnstileFailed = ref(false)
let widgetId = null
let failTimer = null

// ── Turnstile ────────────────────────────────────────────────────────
// Cloudflare's documented onload pattern — render only once the API is
// actually ready (no polling race), wrapped so a render error can't wedge
// the widget, with a fallback if the script is blocked (ad-blockers).
function doRender() {
  if (widgetId !== null || !window.turnstile || !widgetEl.value) return
  try {
    widgetId = window.turnstile.render(widgetEl.value, {
      sitekey: SITE_KEY,
      callback: (t) => { token.value = t; turnstileFailed.value = false; clearTimeout(failTimer) },
      'expired-callback': () => { token.value = '' },
      'error-callback': () => { token.value = '' },
      theme: 'auto',
    })
  } catch (err) {
    console.warn('[demo] turnstile render failed', err)
    turnstileFailed.value = true
  }
}
onMounted(() => {
  if (!SITE_KEY) return
  window.__psnapTurnstileOnload = doRender
  if (window.turnstile) { doRender() } else {
    const id = 'cf-turnstile-script'
    if (!document.getElementById(id)) {
      const s = document.createElement('script')
      s.id = id
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__psnapTurnstileOnload'
      s.async = true
      s.defer = true
      s.onerror = () => { turnstileFailed.value = true }
      document.head.appendChild(s)
    }
  }
  // If no token has arrived in 12s, treat verification as unavailable so the
  // button isn't permanently dead.
  failTimer = setTimeout(() => { if (!token.value) turnstileFailed.value = true }, 12000)
})
onBeforeUnmount(() => {
  clearTimeout(failTimer)
  if (widgetId !== null && window.turnstile) window.turnstile.remove(widgetId)
})

// Ready when a file is chosen and either we have a token or verification is
// unavailable (the backend's IP + budget caps still apply).
const ready = computed(() =>
  !!file.value && !loading.value && (!SITE_KEY || !!token.value || turnstileFailed.value))

async function pickPages(f) {
  pageCount.value = 0
  if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') { pageCount.value = 1; return }
  try {
    const pdfjs = await import('pdfjs-dist')
    const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
    const doc = await pdfjs.getDocument({ data: await f.arrayBuffer() }).promise
    pageCount.value = doc.numPages
  } catch { pageCount.value = 1 }
}

function setFile(f) {
  error.value = ''
  result.value = null
  if (!f) return
  if (f.size > MAX_BYTES) { error.value = 'Demo files are capped at 3 MB. Sign up free to process larger documents.'; return }
  file.value = f
  pickPages(f)
}
function onDrop(e) { dragOver.value = false; setFile(e.dataTransfer?.files?.[0]) }
function onPick(e) { setFile(e.target.files?.[0]) }

function fileToBase64(f) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).split(',')[1] || '')
    r.onerror = reject
    r.readAsDataURL(f)
  })
}

async function extract() {
  if (!ready.value) return
  loading.value = true
  error.value = ''
  try {
    const fileBase64 = await fileToBase64(file.value)
    const contentType = file.value.type || (/\.pdf$/i.test(file.value.name) ? 'application/pdf' : 'application/octet-stream')
    const { data } = await api.post('/demo/extract', { fileBase64, contentType, turnstileToken: token.value })
    result.value = data
  } catch (err) {
    error.value = err?.response?.data?.detail || 'We couldn’t extract this document. Please try another file.'
    // reset Turnstile (single-use token)
    if (widgetId !== null && window.turnstile) { window.turnstile.reset(widgetId); token.value = '' }
  } finally {
    loading.value = false
  }
}

function reset() {
  file.value = null
  result.value = null
  error.value = ''
  pageCount.value = 0
}

const totalsReconcile = computed(() => result.value?.checks?.arithmetic === 'passed')
const lineReconcile = computed(() => result.value?.checks?.line_arithmetic === 'passed')
const confClass = (c) => (c >= 90 ? 'ok' : c >= 70 ? 'med' : 'low')
</script>

<template>
  <div class="card-surface demo-try">
    <!-- Empty / upload state -->
    <div v-if="!result">
      <div
        class="drop"
        :class="dragOver ? 'over' : ''"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="onDrop"
      >
        <input id="demo-file" type="file" :accept="ACCEPT" class="sr-only" @change="onPick">
        <UploadCloud class="h-8 w-8 text-primary" />
        <p class="mt-2 font-medium">
          <label for="demo-file" class="cursor-pointer text-primary underline-offset-2 hover:underline">Choose a document</label>
          or drag it here
        </p>
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">PDF, PNG, JPEG or TIFF · up to 3 MB · we extract the first page</p>
        <p v-if="file" class="mt-3 inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-sm dark:bg-slate-800">
          <FileText class="h-3.5 w-3.5" /> {{ file.name }}
          <span v-if="pageCount > 1" class="text-xs text-slate-500">· first of {{ pageCount }} pages</span>
        </p>
      </div>

      <div v-show="!turnstileFailed" ref="widgetEl" class="mt-4 flex justify-center" />
      <p v-if="turnstileFailed" class="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
        Verification couldn’t load (an ad-blocker may be blocking it). You can still try —
        or <RouterLink to="/signup" class="text-primary hover:underline">sign up free</RouterLink>.
      </p>

      <p v-if="error" class="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">{{ error }}</p>

      <button type="button" class="btn-primary mt-4 w-full" :disabled="!ready" @click="extract">
        <Loader2 v-if="loading" class="h-4 w-4 animate-spin" />
        {{ loading ? 'Extracting…' : 'Extract my document — free' }}
      </button>
      <p class="mt-2 flex items-center justify-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
        <ShieldCheck class="h-3.5 w-3.5" /> No account. Your document is processed in memory and never stored.
      </p>
    </div>

    <!-- Result state -->
    <div v-else>
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <span class="pill bg-primary/10 text-xs capitalize text-primary">{{ (result.docType || 'document').replace('_', ' ') }}</span>
          <span class="text-sm text-slate-500 dark:text-slate-400">{{ result.confidence }}% confidence</span>
        </div>
        <button type="button" class="text-sm text-primary hover:underline" @click="reset">Try another</button>
      </div>

      <p v-if="totalsReconcile || lineReconcile" class="mt-2 inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
        <BadgeCheck class="h-4 w-4" />
        {{ totalsReconcile ? 'Totals reconcile' : 'Line items verified' }} — the math checks out.
      </p>

      <ul class="mt-3 divide-y divide-slate-200 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
        <li v-for="f in result.fields.slice(0, 8)" :key="f.key" class="flex items-center justify-between gap-3 px-3 py-2 text-sm">
          <span class="text-slate-500 dark:text-slate-400">{{ f.label || f.key }}</span>
          <span class="flex items-center gap-2">
            <span class="font-medium">{{ f.value || '—' }}</span>
            <span class="conf" :class="confClass(f.confidence)">{{ f.confidence }}%</span>
          </span>
        </li>
      </ul>
      <p v-if="result.fields.length > 8" class="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
        + {{ result.fields.length - 8 }} more field{{ result.fields.length - 8 === 1 ? '' : 's' }}{{ result.lineItems.length ? `, ${result.lineItems.length} line items` : '' }}
      </p>

      <!-- Download gate -->
      <div class="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-4 text-center dark:bg-primary/10">
        <Lock class="mx-auto h-5 w-5 text-primary" />
        <p class="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">Download as JSON or CSV with a free account</p>
        <p class="mt-0.5 text-xs text-slate-600 dark:text-slate-400">Plus 5 pages a month, history, and your own custom fields.</p>
        <RouterLink to="/signup" class="btn-primary mt-3 inline-flex">
          Sign up free to download <ArrowRight class="h-4 w-4" />
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
.demo-try { padding: 1.25rem; }
.drop {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  border: 2px dashed rgb(148 163 184 / 0.5); border-radius: 0.75rem;
  padding: 1.75rem 1rem; text-align: center; transition: border-color .15s, background .15s;
}
.drop.over { border-color: rgb(var(--color-primary)); background: rgb(var(--color-primary) / 0.05); }
.conf { font-size: .7rem; font-weight: 600; padding: .05rem .35rem; border-radius: .3rem; }
.conf.ok { color: rgb(5 150 105); background: rgb(5 150 105 / 0.1); }
.conf.med { color: rgb(217 119 6); background: rgb(217 119 6 / 0.1); }
.conf.low { color: rgb(225 29 72); background: rgb(225 29 72 / 0.1); }
</style>
